import React, { useState, useEffect } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { TrendingUp, TrendingDown, DollarSign, AlertCircle, Lightbulb, Home, Sparkles } from 'lucide-react';

const SIPvsEMIPage = () => {
  const [inputs, setInputs] = useState({
    amount: 10000000,
    years: 15,
    loanInterest: 10,
    sipReturn: 12
  });
  const [comparison, setComparison] = useState(null);

  useEffect(() => {
    calculateComparison();
  }, [inputs]);

  const calculateComparison = () => {
    const amount = parseFloat(inputs.amount);
    const months = parseInt(inputs.years) * 12;
    const loanRate = parseFloat(inputs.loanInterest) / 12 / 100;
    const sipRate = parseFloat(inputs.sipReturn) / 12 / 100;

    // EMI Calculation
    const emi = amount * loanRate * (Math.pow(1 + loanRate, months)) / (Math.pow(1 + loanRate, months) - 1);
    const totalEMIPaid = emi * months;
    const totalInterest = totalEMIPaid - amount;

    // SIP Calculation
    const sipFutureValue = emi * (((Math.pow(1 + sipRate, months) - 1) / sipRate) * (1 + sipRate));
    const sipInvested = emi * months;
    const sipReturns = sipFutureValue - sipInvested;

    const advantage = sipFutureValue - amount;

    setComparison({
      emi: {
        monthlyPayment: Math.round(emi),
        totalPaid: Math.round(totalEMIPaid),
        interest: Math.round(totalInterest),
        assetValue: amount
      },
      sip: {
        monthlyInvestment: Math.round(emi),
        futureValue: Math.round(sipFutureValue),
        invested: Math.round(sipInvested),
        returns: Math.round(sipReturns)
      },
      advantage: Math.round(advantage),
      savings: Math.round(totalInterest + sipReturns)
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      <Navbar />
      
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              SIP vs EMI Calculator
            </h1>
            <p className="text-lg text-white/70 max-w-3xl mx-auto">
              Compare wealth creation through SIP vs spending on EMI
            </p>
          </div>

          {/* Input Controls */}
          <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10 mb-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <label className="text-white text-sm mb-2 block">Purchase Amount</label>
                <input
                  type="number"
                  value={inputs.amount}
                  onChange={(e) => setInputs({...inputs, amount: e.target.value})}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="text-white text-sm mb-2 block">Years</label>
                <input
                  type="number"
                  value={inputs.years}
                  onChange={(e) => setInputs({...inputs, years: e.target.value})}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="text-white text-sm mb-2 block">Loan Interest</label>
                <input
                  type="number"
                  step="0.5"
                  value={inputs.loanInterest}
                  onChange={(e) => setInputs({...inputs, loanInterest: e.target.value})}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="text-white text-sm mb-2 block">SIP Return</label>
                <input
                  type="number"
                  step="0.5"
                  value={inputs.sipReturn}
                  onChange={(e) => setInputs({...inputs, sipReturn: e.target.value})}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Comparison Boxes */}
          {comparison && (
            <div className="space-y-8">
              <div className="grid lg:grid-cols-2 gap-8">
                {/* EMI Box */}
                <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 backdrop-blur-xl rounded-3xl p-8 border border-red-500/30">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-red-500/20 rounded-xl">
                        <TrendingDown className="w-6 h-6 text-red-400" />
                      </div>
                      <h3 className="text-2xl font-bold text-white">EMI Route</h3>
                    </div>
                    <span className="px-4 py-1 bg-red-500/20 rounded-full text-red-400 text-sm font-semibold">Debt</span>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-white/5 rounded-xl p-4">
                      <p className="text-white/50 text-sm mb-1">Monthly EMI</p>
                      <p className="text-3xl font-bold text-white">{formatCurrency(comparison.emi.monthlyPayment)}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white/5 rounded-xl p-4">
                        <p className="text-white/50 text-xs mb-1">Total Paid</p>
                        <p className="text-lg font-bold text-white">{formatCurrency(comparison.emi.totalPaid)}</p>
                      </div>
                      <div className="bg-white/5 rounded-xl p-4">
                        <p className="text-white/50 text-xs mb-1">Interest</p>
                        <p className="text-lg font-bold text-red-400">{formatCurrency(comparison.emi.interest)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SIP Box */}
                <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 backdrop-blur-xl rounded-3xl p-8 border border-green-500/30">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-green-500/20 rounded-xl">
                        <TrendingUp className="w-6 h-6 text-green-400" />
                      </div>
                      <h3 className="text-2xl font-bold text-white">SIP Route</h3>
                    </div>
                    <span className="px-4 py-1 bg-green-500/20 rounded-full text-green-400 text-sm font-semibold">Wealth</span>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-white/5 rounded-xl p-4">
                      <p className="text-white/50 text-sm mb-1">Monthly SIP</p>
                      <p className="text-3xl font-bold text-white">{formatCurrency(comparison.sip.monthlyInvestment)}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white/5 rounded-xl p-4">
                        <p className="text-white/50 text-xs mb-1">Invested</p>
                        <p className="text-lg font-bold text-white">{formatCurrency(comparison.sip.invested)}</p>
                      </div>
                      <div className="bg-white/5 rounded-xl p-4">
                        <p className="text-white/50 text-xs mb-1">Returns</p>
                        <p className="text-lg font-bold text-green-400">{formatCurrency(comparison.sip.returns)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Advantage */}
              <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-xl rounded-3xl p-8 border border-blue-500/30 text-center">
                <DollarSign className="w-16 h-16 text-blue-400 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-2">SIP Advantage</h3>
                <p className="text-5xl font-bold text-green-400 mb-4">{formatCurrency(comparison.advantage)}</p>
                <p className="text-white/70">Extra wealth after buying the asset!</p>
              </div>

              {/* Motivating Disclaimer */}
              <div className="bg-gradient-to-br from-amber-500/10 via-yellow-500/10 to-orange-500/10 backdrop-blur-xl rounded-3xl p-8 border border-amber-500/30">
                <div className="flex items-start gap-4 mb-6">
                  <div className="p-3 bg-amber-500/20 rounded-xl flex-shrink-0">
                    <Lightbulb className="w-8 h-8 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
                      Think Before You Borrow
                      <Sparkles className="w-5 h-5 text-amber-400" />
                    </h3>
                    <p className="text-amber-200/80 text-lg">The Hidden Cost of EMIs That Nobody Tells You</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="bg-black/30 rounded-2xl p-6 border border-amber-500/20">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <Home className="w-6 h-6 text-red-400" />
                        <span className="text-white font-semibold">EMI Path:</span>
                      </div>
                      <span className="text-red-400 font-bold">1 House + Heavy Interest Burden</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Home className="w-6 h-6 text-green-400" />
                        <Home className="w-6 h-6 text-green-400" />
                        <span className="text-white font-semibold">SIP Path:</span>
                      </div>
                      <span className="text-green-400 font-bold">Potentially 2 Houses + Financial Freedom!</span>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/20">
                      <p className="text-red-400 font-semibold mb-2">❌ The EMI Trap</p>
                      <ul className="text-white/70 text-sm space-y-1">
                        <li>• Pay almost <span className="text-red-400 font-bold">double</span> the actual price</li>
                        <li>• Interest goes to the bank, not you</li>
                        <li>• Stuck with one depreciating asset</li>
                        <li>• Financial stress for {inputs.years}+ years</li>
                      </ul>
                    </div>
                    <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/20">
                      <p className="text-green-400 font-semibold mb-2">✅ The SIP Advantage</p>
                      <ul className="text-white/70 text-sm space-y-1">
                        <li>• Money works <span className="text-green-400 font-bold">for you</span>, not against</li>
                        <li>• Compounding creates real wealth</li>
                        <li>• Buy the asset later with cash</li>
                        <li>• Have surplus for another investment!</li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl p-5 border border-green-500/30">
                    <p className="text-white text-center">
                      <span className="text-2xl">💡</span>{" "}
                      <span className="font-bold text-green-400">Smart Insight:</span>{" "}
                      <span className="text-white/90">
                        The interest you pay on a ₹50 Lakh home loan over 20 years could be <span className="text-green-400 font-bold">₹50-60 Lakhs</span> — 
                        that's almost another house! Invest wisely through SIP, buy with cash later, 
                        and you could own <span className="text-green-400 font-bold">2 properties</span> instead of 1.
                      </span>
                    </p>
                  </div>

                  <div className="text-center">
                    <p className="text-amber-300/90 text-sm italic">
                      "The rich invest first and spend what's left. The poor spend first and invest what's left."
                    </p>
                    <p className="text-white/50 text-xs mt-2">— Warren Buffett</p>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-white/10">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <p className="text-white/50 text-xs">
                      <span className="text-amber-400 font-semibold">Disclaimer:</span> This calculator is for educational and illustrative purposes only. 
                      Actual returns may vary based on market conditions. Past performance does not guarantee future results. 
                      Consider consulting a certified financial advisor before making investment decisions. 
                      Real estate and SIP investments have different risk profiles and liquidity characteristics.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SIPvsEMIPage;